import { isAdminPath, isFunnelPath } from '@/components/admin/ChromeFrame';

export interface SupportMessengerGateInput {
  isLoggedIn: boolean;
  pathname: string;
  /** VITE_SUPPORT_APP_ID — when unset the whole feature is a no-op (CLAUDE.md: no partial
   *  rollout of a third-party widget without its config). */
  hasAppId: boolean;
}

/**
 * Pure decision: should the support messenger be booted right now? Anonymous visitors get
 * no branch here on purpose — an anonymous IDENTIFIED-path boot is deferred until a
 * cookie-consent manager is wired up (see the customer-support handover doc's decisions,
 * 2026-07-21); today, logged-out always means "do nothing" here, not "boot anonymously".
 * (The separate on-demand guest button — shouldOfferGuestSupportButton below — is the
 * explicit-request exception to that, scoped to allow-listed pages only.)
 *
 * The signup/onboarding funnel is excluded (Maciek, 2026-07-28): a support launcher floating
 * over the flow we are asking someone to complete is a distraction at exactly the wrong
 * moment, and these screens are a few taps each — nobody needs live chat to get through them.
 * It comes back the moment they land in the product. `isFunnelPath` is the SAME predicate the
 * viewport shell uses, so "what counts as the funnel" has one definition.
 */
export function shouldBootSupportMessenger(input: SupportMessengerGateInput): boolean {
  if (!input.hasAppId) return false;
  if (isAdminPath(input.pathname)) return false;
  if (isFunnelPath(input.pathname)) return false;
  return input.isLoggedIn;
}

/**
 * The claim-landing family of pages (/claim/:token, /claim/success, …) — public,
 * unauthenticated-visitor pages where a guest may reasonably need support before ever creating
 * an account. Deliberately narrow: does NOT match /owner/claim/review (a logged-in owner page)
 * or /wlasciciel/:token (a different landing page, out of scope per Piotr's standing decision —
 * see the customer-support handover doc). Mirrors isAdminPath's exact-segment-prefix shape.
 */
export function isClaimPath(pathname: string): boolean {
  return pathname === '/claim' || pathname.startsWith('/claim/');
}

export interface SupportGuestGateInput {
  isLoggedIn: boolean;
  pathname: string;
  hasAppId: boolean;
  /** True once EITHER an identified or a guest session has already booted for this tab — the
   *  button's whole point is to disappear once the vendor's own launcher takes over. */
  alreadyBooted: boolean;
}

/**
 * Pure decision: should the discreet "Need help?" guest button be offered right now? This is
 * the "on explicit request" carve-out (2026-07-21 decision, see the customer-support handover
 * doc's decisions) — no widget code loads and no cookie is set until the visitor clicks it
 * themselves, so it needs no consent banner. Scoped to allow-listed pages only (claim-landing
 * family) and only for
 * logged-out visitors — a logged-in user gets the identified path instead (see
 * shouldBootSupportMessenger), never this button.
 */
export function shouldOfferGuestSupportButton(input: SupportGuestGateInput): boolean {
  if (!input.hasAppId) return false;
  if (input.isLoggedIn) return false;
  if (input.alreadyBooted) return false;
  return isClaimPath(input.pathname);
}
